import { useIntl } from 'react-intl';
import { Badge } from 'components/ui/atoms/badge';
import { Button } from 'components/ui/atoms/button';
import { Card } from 'components/ui/atoms/card';
import { formatDate } from 'utils/format-date';
import { messages } from '../messages';
import { DiscountProductCardProps } from './types';

const COMMERCE_COORDINATOR_BASE_URL =
  process.env.NEXT_PUBLIC_EDX_COMMERCE_COORDINATOR_BASE_URL!;
const PAYMENT_PAGE_REDIRECT_URL = `${COMMERCE_COORDINATOR_BASE_URL}/lms/payment_page_redirect/`;

export const DiscountProductCard = ({
  product,
  discountCode,
}: DiscountProductCardProps) => {
  const { formatMessage } = useIntl();

  let discountType = '';
  let discountCurrency = '$';
  let isEnrollment = false;

  if (product.discount.type === 'relative') {
    discountType = '%';
    discountCurrency = '';
    if (product.discount.value === 100) {
      isEnrollment = true;
    }
  }

  const truncatedProductTitle = (title: string) =>
    title.length > 40 ? `${title.slice(0, 40)}...` : title;

  const footer = (
    <>
      <span className="product-price">
        <s>${product.totalPrice}</s>
      </span>{' '}
      <span className="discounted-price">
        {formatMessage(messages['redeem.discount.product.discount.now'])}$
        {product.discountedPrice}
      </span>
    </>
  );

  const redirectParams = new URLSearchParams({
    course_run_key: product.courseRunKey,
    code: discountCode,
  });

  return (
    <Card className="p-0">
      <div className="tw-flex md:tw-flex-row tw-flex-col md:tw-h-60">
        <div className="tw-relative">
          <Card.ImageCap
            src={product.imageUrl}
            srcAlt="Course image"
            className={!product.imageUrl ? 'md:tw-border-r-2' : ''}
            orientation="horizontal"
          />

          <Badge
            variant="success"
            className="tw-absolute tw-top-2 tw-left-2 tw-z-50 !tw-text-lg !tw-p-2"
          >
            {`${discountCurrency}${product.discount.value}${discountType} ${formatMessage(messages['redeem.discount.product.off'])}`}
          </Badge>

          {product.discountValidUntil && (
            <div className="tw-absolute tw-bottom-0 tw-left-0 tw-w-full tw-pt-5 tw-text-xs tw-font-medium tw-text-center tw-text-black tw-bg-[linear-gradient(to_top,rgba(255,255,255,0.85)_0%,rgba(255,255,255,0.75)_30%,rgba(255,255,255,0.7)_50%,transparent_100%)] tw-py-[0.4375rem]">
              {formatMessage(messages['redeem.discount.product.discount.end'])}
              {formatDate(product.discountValidUntil)}
            </div>
          )}
        </div>
        <div className="tw-flex tw-flex-col tw-flex-grow tw-justify-evenly">
          <Card.Header className="tw-pt-8 !tw-px-6">
            <Card.Title className="!tw-font-bold">
              {truncatedProductTitle(product.title)}
            </Card.Title>
            <Card.Description className="tw-pt-6">
              {product.organization ?? ' '}
            </Card.Description>
          </Card.Header>
          <Card.Content className="tw-text-md !tw-p-6">
            {formatMessage(messages['redeem.discount.product.course.start'])}{' '}
            {product.courseStartDate && formatDate(product.courseStartDate)}
          </Card.Content>

          <Card.Footer className="tw-flex sm:tw-flex-row tw-flex-col-reverse sm:tw-justify-between sm:tw-gap-2.5 tw-gap-6 sm:tw-w-full tw-border-t-0 tw-px-6 tw-pb-6 tw-pt-8">
            <span className="tw-text-md">{footer}</span>
            <Button className="tw-w-full sm:tw-w-auto" asChild>
              <a
                href={`${PAYMENT_PAGE_REDIRECT_URL}?${redirectParams.toString()}`}
              >
                {formatMessage(
                  isEnrollment
                    ? messages['redeem.discount.product.discount.enrollment']
                    : messages['redeem.discount.product.discount.checkout'],
                )}
              </a>
            </Button>
          </Card.Footer>
        </div>
      </div>
    </Card>
  );
};
